import React, { useState } from 'react';
import {
    Briefcase,
    ChevronLeft,
    Upload,
    CheckCircle2,
    AlertCircle,
    RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/hooks/useAuth';
import { useKYC } from '@/shared/hooks/useKYC';
import { cn } from '@/shared/lib/utils';
import { uploadKYCImage } from '@/shared/utils/uploadImage';

const BusinessVerificationPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { submitApplication } = useKYC(user?.uid);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [companyName, setCompanyName] = useState('');
    const [registrationNumber, setRegistrationNumber] = useState('');
    const [isIndividual, setIsIndividual] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async () => {
        // 개인회원인 경우 사업자 정보 없이도 제출 허용
        if (!isIndividual && (!selectedFile || !companyName || !registrationNumber)) {
            alert('모든 필수 정보를 입력해주세요.');
            return;
        }

        setIsSubmitting(true);
        try {
            let imageUrl = null;

            // 사업자가 아닌 경우 이미지 업로드 스킵
            if (!isIndividual && selectedFile) {
                const uploadTimeout = new Promise<never>((_, reject) =>
                    setTimeout(() => reject(new Error('업로드 시간이 초과되었습니다. 네트워크를 확인하고 다시 시도해주세요.')), 60000)
                );

                imageUrl = await Promise.race([
                    uploadKYCImage(selectedFile, user!.uid, 'BUSINESS'),
                    uploadTimeout
                ]);
            }

            // Submit application with real image URL or N/A for individual
            const result = await submitApplication('BUSINESS', {
                isIndividual,
                companyName: isIndividual ? '개인회원' : companyName,
                registrationNumber: isIndividual ? 'N/A' : registrationNumber,
                fileName: selectedFile?.name || 'N/A',
                fileSize: selectedFile?.size || 0,
                fileType: selectedFile?.type || 'N/A',
                imageUrl: imageUrl || 'N/A'
            });

            if (result.success) {
                alert('기업 인증 신청이 완료되었습니다. 관리자 승인 후 반영됩니다.');
                navigate('/verification');
            } else {
                alert('신청 중 오류가 발생했습니다. 다시 시도해주세요.');
            }
        } catch (error) {
            console.error('Image upload error:', error);
            const msg = error instanceof Error ? error.message : '이미지 업로드 중 오류가 발생했습니다.';
            alert(msg + '\n다시 시도해주세요.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-8 animate-in fade-in duration-700">
            <button
                onClick={() => navigate('/verification')}
                className="flex items-center gap-2 text-slate-500 hover:text-white transition-colors text-sm font-bold group"
            >
                <ChevronLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" /> 뒤로가기
            </button>

            <div className="space-y-2">
                <h1 className="text-3xl font-black text-white flex items-center gap-3">
                    <Briefcase className="h-8 w-8 text-amber-500" /> 기업/사업자 인증
                </h1>
                <p className="text-slate-500 font-bold text-sm">사업자 등록증과 기업 정보를 입력하여 법인/사업자 계정으로 전환하세요.</p>
            </div>

            <div className="rounded-[2.5rem] bg-card border border-border p-8 shadow-2xl space-y-8">
                {/* 개인회원 체크박스 */}
                <label className="flex items-center gap-3 cursor-pointer p-4 rounded-2xl bg-accent/30 border border-border hover:bg-accent/50 transition-colors">
                    <input
                        type="checkbox"
                        checked={isIndividual}
                        onChange={(e) => setIsIndividual(e.target.checked)}
                        className="w-5 h-5 rounded border-border bg-background checked:bg-amber-500 cursor-pointer"
                    />
                    <span className="text-sm font-bold text-foreground uppercase tracking-wide">
                        사업자가 아닙니다 (개인회원)
                    </span>
                </label>

                {/* 사업자 정보 입력 필드: isIndividual === false일 때만 표시 */}
                {!isIndividual && (
                    <div className="space-y-6">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest px-1">기업명 (상호)</label>
                            <input
                                type="text"
                                placeholder="사업자 등록증 상의 법인명"
                                value={companyName}
                                onChange={(e) => setCompanyName(e.target.value)}
                                className="w-full h-14 bg-white/5 border border-white/5 rounded-2xl px-6 text-sm font-bold text-white outline-none focus:border-amber-500/50 transition-all"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest px-1">사업자 등록번호</label>
                            <input
                                type="text"
                                placeholder="000-00-00000"
                                value={registrationNumber}
                                onChange={(e) => setRegistrationNumber(e.target.value)}
                                className="w-full h-14 bg-white/5 border border-white/5 rounded-2xl px-6 text-sm font-bold text-white outline-none focus:border-amber-500/50 transition-all"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest px-1">사업자 등록증 사본</label>
                            <div className="relative group h-48">
                                <input
                                    type="file"
                                    accept=".jpg,.png,.pdf"
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                            setSelectedFile(e.target.files[0]);
                                        }
                                    }}
                                />
                                <div className={cn(
                                    "h-full border-2 border-dashed rounded-3xl flex flex-col items-center justify-center gap-4 transition-all duration-300",
                                    selectedFile ? "border-amber-500/50 bg-amber-500/5" : "border-white/5 bg-white/[0.02] group-hover:border-amber-500/30 group-hover:bg-amber-500/5"
                                )}>
                                    {selectedFile ? (
                                        <>
                                            <CheckCircle2 className="h-8 w-8 text-amber-500" />
                                            <p className="text-sm font-black text-white">{selectedFile.name}</p>
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="h-8 w-8 text-slate-400" />
                                            <p className="text-sm font-black text-white">서류 업로드</p>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="p-6 rounded-3xl bg-amber-500/5 border border-amber-500/10 flex items-start gap-4">
                    <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                        <p className="text-xs font-black text-amber-500">혜택 안내</p>
                        <p className="text-[10px] font-bold text-slate-500 leading-relaxed">
                            기업 인증 완료 시 법인 전용 입출금 한도가 적용되며, 대량 이체 및 API 서비스 사용 권한이 부여됩니다.
                        </p>
                    </div>
                </div>

                <button
                    onClick={handleSubmit}
                    disabled={(!isIndividual && (!selectedFile || !companyName || !registrationNumber)) || isSubmitting}
                    className={cn(
                        "w-full h-16 rounded-2xl font-black shadow-xl transition-all uppercase tracking-widest text-sm flex items-center justify-center gap-3",
                        "bg-amber-600 text-white shadow-amber-600/20 hover:bg-amber-500",
                        "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-amber-600"
                    )}
                >
                    {isSubmitting ? <RefreshCw className="h-5 w-5 animate-spin" /> : "기업 인증 신청하기"}
                </button>
            </div>
        </div>
    );
};

export default BusinessVerificationPage;
